const _ = require("lodash");
const LoadState = require("../../core/vault/util/LoadState");
const {setIn, extendIn} = require("../../../shared/ImmHelpers");
const GradeChapter = require("../../../shared/GradeChapter");
const OpsLab = require("../../lab/vault/OpsLab");
const ErrorUtil = require("../../../shared/ErrorUtil");

// Note: This object currently does not manage the lock overrides of academy
// tests (aka book tests). The state for this is on the testBlockInfo object
// returned by report:getClasses.

// Resets loaded lock data for all users. Pass the value at the atAllLocks
// state cursor (instead of the user or class locks that the other functions
// want). The keys removed here need to be kept in sync with the cursors file.
const resetAllUsers = function(lovs) {
	const userKeys = Object.keys(lovs).filter(k => _.startsWith(k, "user"));
	if (!userKeys.length) return lovs;
	return _.omit(lovs, userKeys);
};

const initUser = function(lov, userID) {
	if (lov && lov.userID === userID) {
		return lov;
	}
	return LoadState.loadInit({
		userID,
		classID: null,
		blocksInChapters: null,
		blocksInLabTypes: null,
		lockData: null,
	});
};

const initClass = function(lov, classID) {
	if (lov && lov.classID === classID) {
		return lov;
	}
	return LoadState.loadInit({
		userID: null,
		classID,
		blocksInChapters: null,
		blocksInLabTypes: null,
		lockData: null,
	});
};

const loadStart = function(lov, api) {
	return LoadState.loadStart(lov, () =>
		api.loadLocks({userID: lov.userID, classID: lov.classID})
	);
};

const setLoadedData = function(lov, response) {
	if (response.lockData) {
		lov = setIn(lov, ["lockData"], response.lockData);
	}
	if (response.blocksInChapters) {
		lov = setIn(lov, ["blocksInChapters"], response.blocksInChapters);
	}
	if (response.blocksInLabTypes) {
		lov = setIn(lov, ["blocksInLabTypes"], response.blocksInLabTypes);
	}
	return lov;
};

const getLoadID = function(lov) {
	if (lov.classID) {
		return `lockOverrides-c${lov.classID}`;
	} else {
		return `lockOverrides-u${lov.userID}`;
	}
};

// In all of these next functions, objectType is one of "grade", "chapter",
// "block". objectID is then the gradeID, chapterID, or blockID in the
// respective cases.

const getLockInfoPath = function(objectType, objectID) {
	return ["lockData", objectType + "s", objectID];
};

// Get the lock object, or null if not found.
const getLockInfo = function(lov, objectType, objectID) {
	return _.get(lov, getLockInfoPath(objectType, objectID), null);
};

// Get the value that a lock override should change to if the UI were clicked.
// Returns {newValue, error}. If error is given, newValue should be the same
// as the current one.
const getChangeValue = function(lov, objectType, objectID) {
	const lockObj = getLockInfo(lov, objectType, objectID);
	if (!lockObj) {
		return {newValue: null, error: "No lock info at this location"};
	}

	if (lov.userID) {
		if (!lockObj.lockOverride) {
			return {newValue: lOpposite(lockObj.progressLock), error: null};
		} else if (
			lockObj.lockOverrideParent &&
			lockObj.lockOverrideSource === "student"
		) {
			const parent = getDisplayedObjectType(lockObj.lockOverrideParent);
			const child = getDisplayedObjectType(objectType);
			const error = `The ${parent} must first be unlocked to unlock this ${child}.`;
			return {newValue: lockObj.lockOverride, error};
		} else if (
			lockObj.lockOverrideSource === "student" ||
			lockObj.lockOverrideSource === "assigned"
		) {
			return {newValue: null, error: null};
		} else if (lockObj.lockOverrideSource === "class") {
			return {newValue: lOpposite(lockObj.lockOverride), error: null};
		} else {
			const data = {lockObj, objectType, objectID, userID: lov.userID};
			ErrorUtil.log(
				"E_BAD_LOCK_OBJ",
				"Lock object used by override UI had unrecognized set of values.",
				data
			);
			// No error to avoid locking up the UI in this case. There's a lock
			// override set, so we probably just want to unset it.
			return {newValue: null, error: null};
		}
	} else if (lov.classID) {
		if (lockObj.lockOverrideParent) {
			const parent = getDisplayedObjectType(lockObj.lockOverrideParent);
			const child = getDisplayedObjectType(objectType);
			const error = `The ${parent} must first be unlocked to unlock this ${child}.`;
			return {newValue: lockObj.lockOverride, error};
		} else {
			return {newValue: lCycle(lockObj.lockOverride), error: null};
		}
	}
	return {newValue: null, error: "No user ID or class ID loaded."};
};

const getDisplayedObjectType = function(objectType) {
	const nameMap = {
		grade: "level",
		block: "lesson",
		labType: "puzzle type",
		labBlock: "puzzle set",
		space: "Puzzle Lab",
		test: "test",
	};
	return nameMap[objectType] || objectType;
};

// newValue should be one of null, "locked", "unlocked". In general a UI should
// only pass the return of getNextValue, but this accepts all values. api is
// optional; the request is not made if it's not provided.
const setValue = function(lov, api, objectType, objectID, newValue) {
	const lockObj = getLockInfo(lov, objectType, objectID);
	if (!lockObj) {
		ErrorUtil.log(
			"E_BAD_LOCK_LOC",
			"Tried to set a nonexistent lock override.",
			{
				objectType,
				objectID,
				userID: lov.userID,
				classID: lov.classID,
			}
		);
		return lov;
	}
	let reloadNeeded = false;

	if (lov.userID) {
		if (lockObj.studentLockOverride === newValue) {
			return lov;
		}

		// We only update the currently edited object on the frontend before
		// triggering a reload if there are any descendants. There are too many
		// strange progress lock interactions and too much missing information.

		const parentInfo = getParent(lov, objectType, objectID);
		const parentObj =
			parentInfo &&
			getLockInfo(lov, parentInfo.objectType, parentInfo.objectID);

		const newLockObj = _.clone(lockObj);
		newLockObj.studentLockOverride = newValue;
		if (
			lockObj.lockOverrideParent &&
			lockObj.lockOverrideSource === "student"
		) {
			// No parent changes
		} else if (
			newValue === null &&
			parentObj &&
			parentObj.lockOverride === "locked"
		) {
			newLockObj.lockOverride = "locked";
			newLockObj.lockOverrideSource = parentObj.lockOverrideSource;
			newLockObj.lockOverrideParent =
				parentObj.lockOverrideParent || parentInfo.objectType;
		} else {
			newLockObj.lockOverride = newValue;
			newLockObj.lockOverrideSource = newValue ? "student" : null;
			newLockObj.lockOverrideParent = null;
		}
		lov = setIn(lov, getLockInfoPath(objectType, objectID), newLockObj);

		if (
			objectType === "grade" ||
			objectType === "chapter" ||
			objectType === "labType" ||
			objectType === "space"
		) {
			reloadNeeded = true;
		}
	} else if (lov.classID) {
		if (lockObj.classLockOverride === newValue) {
			return lov;
		}
		const newLockObj = _.clone(lockObj);
		newLockObj.classLockOverride = newValue;
		newLockObj.classIDWithOverride = newValue ? lov.classID : null;
		if (!lockObj.lockOverrideParent) {
			newLockObj.lockOverride = newValue;
			newLockObj.lockOverrideSource = newValue ? "class" : null;
			newLockObj.lockOverrideParent = null;
		}
		const updates = computeClassDescendantUpdates(
			lov,
			objectType,
			objectID,
			newLockObj
		);
		lov = extendIn(lov, ["lockData", "grades"], updates.grades);
		lov = extendIn(lov, ["lockData", "chapters"], updates.chapters);
		lov = extendIn(lov, ["lockData", "blocks"], updates.blocks);
		lov = extendIn(lov, ["lockData", "labTypes"], updates.labTypes);
		lov = extendIn(lov, ["lockData", "labBlocks"], updates.labBlocks);
		lov = extendIn(lov, ["lockData", "spaces"], updates.spaces);
	}

	const scopeInfo = {userID: lov.userID, classID: lov.classID};
	const itemInfo = {[objectType + "ID"]: objectID};
	api.setLockOverride(scopeInfo, itemInfo, newValue, {
		loadAllLocks: reloadNeeded,
	});
	return lov;
};

// Internal helper. Returns {objectType, objectID} for the parent of the
// passed one, or null if no parent.
const getParent = function(lov, objectType, objectID) {
	if (objectType === "chapter") {
		return {
			objectType: "grade",
			objectID: GradeChapter.chapterIDToGradeID(objectID),
		};
	} else if (objectType === "block") {
		const chapterID = parseInt(
			_.findKey(lov.blocksInChapters, blockIDs =>
				_.includes(blockIDs, objectID)
			)
		);
		return chapterID
			? {
					objectType: "chapter",
					objectID: chapterID,
			  }
			: null;
	} else if (objectType === "labType") {
		return {
			objectType: "space",
			objectID: "lab",
		};
	} else if (objectType === "labBlock") {
		const typeID = parseInt(
			_.findKey(lov.blocksInLabTypes, blockIDs =>
				_.includes(blockIDs, objectID)
			)
		);
		return {
			objectType: "labType",
			objectID: typeID,
		};
	}
	return null;
};

// Internal helper. Returns {grades, chapters, blocks, labTypes, labBlocks}. Each is an object
// mapping IDs with changes to their new lock objects.
const computeClassDescendantUpdates = function(
	lov,
	objectType,
	objectID,
	newLockObj
) {
	// Combines the new parent and old self to compute new self.
	const updateLock = function(parentType, newParentLockObj, oldSelfLockObj) {
		if (newParentLockObj.lockOverride === "locked") {
			return _.extend({}, oldSelfLockObj, {
				lockOverride: "locked",
				lockOverrideSource: "class",
				lockOverrideParent: newParentLockObj.lockOverrideParent || parentType,
			});
		} else {
			const lockOverride = oldSelfLockObj.classLockOverride;
			return _.extend({}, oldSelfLockObj, {
				lockOverride,
				lockOverrideSource: lockOverride ? "class" : null,
				lockOverrideParent: null,
			});
		}
	};

	const ret = {
		grades: {},
		chapters: {},
		blocks: {},
		labTypes: {},
		labBlocks: {},
		spaces: {},
	};
	// More helpers for traversing descendants.
	const updateGradeChapters = function(gradeID) {
		const chapterIDs = GradeChapter.getAllChaptersForGrade(gradeID);
		const parentLockObj = ret.grades[gradeID];
		chapterIDs.forEach(chID => {
			ret.chapters[chID] = updateLock(
				"grade",
				parentLockObj,
				lov.lockData.chapters[chID]
			);
			updateChapterBlocks(chID);
		});
	};
	const updateChapterBlocks = function(chapterID) {
		const blockIDs = lov.blocksInChapters[chapterID];
		const parentLockObj = ret.chapters[chapterID];
		blockIDs.forEach(bID => {
			ret.blocks[bID] = updateLock(
				"chapter",
				parentLockObj,
				lov.lockData.blocks[bID]
			);
		});
	};

	const updateLabTypes = function() {
		const typeIDs = OpsLab.getAllTypeIDs();
		const parentLockObj = ret.spaces.lab;
		typeIDs.forEach(tid => {
			ret.labTypes[tid] = updateLock(
				"space",
				parentLockObj,
				lov.lockData.labTypes[tid]
			);
			updateLabBlocks(tid);
		});
	};
	const updateLabBlocks = function(typeID) {
		const blockIDs = lov.blocksInLabTypes[typeID];
		const parentLockObj = ret.labTypes[typeID];
		blockIDs.forEach(bID => {
			ret.blocks[bID] = updateLock(
				"labType",
				parentLockObj,
				lov.lockData.labBlocks[bID]
			);
		});
	};

	if (objectType === "grade") {
		ret.grades[objectID] = newLockObj;
		updateGradeChapters(objectID);
	} else if (objectType === "chapter") {
		ret.chapters[objectID] = newLockObj;
		updateChapterBlocks(objectID);
	} else if (objectType === "block") {
		ret.blocks[objectID] = newLockObj;
	} else if (objectType === "labType") {
		ret.labTypes[objectID] = newLockObj;
		updateLabBlocks(objectID);
	} else if (objectType === "labBlock") {
		ret.labBlocks[objectID] = newLockObj;
	} else if (objectType === "space") {
		ret.spaces[objectID] = newLockObj;
		updateLabTypes();
	}
	return ret;
};

// Helpers for the three-value system null, "locked", "unlocked"

const lOpposite = function(value) {
	if (value === "locked") return "unlocked";
	if (value === "unlocked") return "locked";
	return null;
};

const lCycle = function(value) {
	if (value === "locked") return "unlocked";
	if (value === "unlocked") return null;
	return "locked";
};

module.exports = {
	resetAllUsers,
	initUser,
	initClass,
	loadStart,
	loadSuccess: LoadState.loadSuccess,
	loadError: LoadState.loadError,
	loadRestart: LoadState.loadRestart,
	setLoadedData,
	getLoadID,
	getLockInfo,
	getChangeValue,
	getDisplayedObjectType,
	setValue,
};
